import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  FileText,
  Activity,
  CheckCircle,
  AlertTriangle,
  XCircle,
  MessageSquare,
  Star,
  Search,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import { Phase2ConsistencyMetrics, type Phase2Metrics } from './components/ConsistencyMetrics';
import { ProviderOverlapMatrix, type SearchExecution } from './components/ProviderOverlapMatrix';

interface TestRun {
  id: string;
  name: string;
  isRevealed: boolean;
  runType: 'MODEL_COMPARISON' | 'MODEL_STABILITY';
  testCase: {
    id: string;
    name: string;
    code: string;
    condition: string;
    patientInfo: {
      age: number;
      gender: string;
      canton?: string;
    };
  };
}

// Stability verdict options (same as Phase 1)
const STABILITY_VERDICTS = [
  {
    value: 'HIGHLY_STABLE',
    label: 'Highly Stable',
    description: 'Same providers across all iterations',
    icon: CheckCircle,
    color: 'text-green-600 bg-green-100 border-green-200',
    score: 100,
  },
  {
    value: 'STABLE',
    label: 'Stable',
    description: 'Minor ranking variations, same providers',
    icon: CheckCircle,
    color: 'text-blue-600 bg-blue-100 border-blue-200',
    score: 80,
  },
  {
    value: 'MODERATE',
    label: 'Moderate',
    description: 'Some provider differences, core overlap',
    icon: AlertTriangle,
    color: 'text-amber-600 bg-amber-100 border-amber-200',
    score: 60,
  },
  {
    value: 'UNSTABLE',
    label: 'Unstable',
    description: 'Significant provider variations',
    icon: AlertTriangle,
    color: 'text-orange-600 bg-orange-100 border-orange-200',
    score: 40,
  },
  {
    value: 'HIGHLY_UNSTABLE',
    label: 'Highly Unstable',
    description: 'Completely different providers each time',
    icon: XCircle,
    color: 'text-red-600 bg-red-100 border-red-200',
    score: 20,
  },
];

// Calculate Phase 2 consistency metrics from search executions
function calculatePhase2Metrics(executions: SearchExecution[]): Phase2Metrics {
  // Build provider appearance map
  const allProviders = new Map<string, Set<string>>(); // providerId -> Set<blindCode>
  const providerRanks = new Map<string, Map<string, number>>(); // providerId -> Map<blindCode, rank>

  // Track which iterations have results
  const iterationsWithResults = executions.filter(
    (exec) => (exec.searchResults?.webResults || []).length > 0
  );
  const iterationsWithResultsCount = iterationsWithResults.length;

  executions.forEach((exec) => {
    const results = exec.searchResults?.webResults || [];
    results.forEach((result, index) => {
      const key = result.doctorId || `${result.name}-${result.institution || ''}`;

      if (!allProviders.has(key)) {
        allProviders.set(key, new Set());
        providerRanks.set(key, new Map());
      }

      allProviders.get(key)!.add(exec.blindCode);
      providerRanks.get(key)!.set(exec.blindCode, index + 1);
    });
  });

  const totalProviders = allProviders.size;
  const totalIterations = executions.length;

  // Calculate overlap based on iterations that HAVE results (not empty ones)
  // This gives a more meaningful metric when some iterations fail/return empty
  const effectiveIterations = Math.max(iterationsWithResultsCount, 1);

  // Providers appearing in ALL iterations that have results
  const commonProviders = Array.from(allProviders.entries()).filter(
    ([_, codes]) => codes.size >= effectiveIterations
  ).length;

  // Provider overlap based on iterations with results
  const providerOverlap = totalProviders > 0 ? (commonProviders / totalProviders) * 100 : 100;

  // Top-3 consistency - only consider iterations with results
  const top3ByExecution = iterationsWithResults.map((exec) => {
    const results = exec.searchResults?.webResults || [];
    return results.slice(0, 3).map((r) => r.doctorId || `${r.name}-${r.institution || ''}`);
  });

  let top3Consistency = 100;
  if (top3ByExecution.length > 0) {
    const top3Union = new Set(top3ByExecution.flat());
    const top3Intersection = Array.from(top3Union).filter((provider) =>
      top3ByExecution.every((top3) => top3.includes(provider))
    );
    top3Consistency =
      top3Union.size > 0 ? (top3Intersection.length / Math.min(3, top3Union.size)) * 100 : 100;
  }

  // Top-5 consistency - only consider iterations with results
  const top5ByExecution = iterationsWithResults.map((exec) => {
    const results = exec.searchResults?.webResults || [];
    return results.slice(0, 5).map((r) => r.doctorId || `${r.name}-${r.institution || ''}`);
  });

  let top5Consistency = 100;
  if (top5ByExecution.length > 0) {
    const top5Union = new Set(top5ByExecution.flat());
    const top5Intersection = Array.from(top5Union).filter((provider) =>
      top5ByExecution.every((top5) => top5.includes(provider))
    );
    top5Consistency =
      top5Union.size > 0 ? (top5Intersection.length / Math.min(5, top5Union.size)) * 100 : 100;
  }

  // Ranking correlation - only compare iterations with results
  let concordantPairs = 0;
  let discordantPairs = 0;

  // Find providers common to all iterations WITH results
  const commonProviderKeys = Array.from(allProviders.entries())
    .filter(([_, codes]) => codes.size >= effectiveIterations)
    .map(([key]) => key);

  if (commonProviderKeys.length >= 2 && iterationsWithResults.length >= 2) {
    // Compare each pair of executions that have results
    for (let i = 0; i < iterationsWithResults.length; i++) {
      for (let j = i + 1; j < iterationsWithResults.length; j++) {
        const exec1 = iterationsWithResults[i];
        const exec2 = iterationsWithResults[j];

        // Compare each pair of common providers
        for (let a = 0; a < commonProviderKeys.length; a++) {
          for (let b = a + 1; b < commonProviderKeys.length; b++) {
            const providerA = commonProviderKeys[a];
            const providerB = commonProviderKeys[b];

            const rankA1 = providerRanks.get(providerA)?.get(exec1.blindCode) || 0;
            const rankB1 = providerRanks.get(providerB)?.get(exec1.blindCode) || 0;
            const rankA2 = providerRanks.get(providerA)?.get(exec2.blindCode) || 0;
            const rankB2 = providerRanks.get(providerB)?.get(exec2.blindCode) || 0;

            const diff1 = rankA1 - rankB1;
            const diff2 = rankA2 - rankB2;

            if ((diff1 > 0 && diff2 > 0) || (diff1 < 0 && diff2 < 0)) {
              concordantPairs++;
            } else if (diff1 !== 0 && diff2 !== 0) {
              discordantPairs++;
            }
          }
        }
      }
    }
  }

  const totalPairs = concordantPairs + discordantPairs;
  const rankingCorrelation =
    totalPairs > 0 ? (concordantPairs - discordantPairs) / totalPairs : 1;

  // Score variance (simplified - just use provider overlap as proxy)
  const scoreVariance = 100 - providerOverlap;

  // Penalize overall metrics if some iterations have no results
  // This is a significant stability issue that should be reflected
  const emptyIterationsCount = totalIterations - iterationsWithResultsCount;

  return {
    providerOverlap,
    top3Consistency,
    top5Consistency,
    rankingCorrelation,
    scoreVariance,
    uniqueProviders: totalProviders,
    commonProviders,
    totalIterations,
    // Add new metrics for visibility
    iterationsWithResults: iterationsWithResultsCount,
    emptyIterations: emptyIterationsCount,
  };
}

export function SearchStabilityEvaluation() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Form state
  const [verdict, setVerdict] = useState<string | null>(null);
  const [verdictReasoning, setVerdictReasoning] = useState('');
  const [observations, setObservations] = useState('');
  const [keyDifferences, setKeyDifferences] = useState<string[]>([]);
  const [newDifference, setNewDifference] = useState('');
  const [confidence, setConfidence] = useState(3);

  // Fetch search results
  const { data: resultsData, isLoading: resultsLoading } = useQuery({
    queryKey: ['validation', 'search-results', id],
    queryFn: () => api.searchPhase.getResults(id!),
    enabled: !!id,
  });

  // Fetch test run info
  const { data: testRunData, isLoading: testRunLoading } = useQuery({
    queryKey: ['validation', 'test-run', id],
    queryFn: () => api.get<{ data: TestRun }>(`/api/validation/test-runs/${id}`),
    enabled: !!id,
  });

  // Fetch existing stability evaluation (to pre-populate form)
  const { data: existingEvalData } = useQuery({
    queryKey: ['validation', 'stability-evaluation', id],
    queryFn: async () => {
      const response = await api.get<{
        testRunId: string;
        currentUser: {
          phase1Completed: boolean;
          phase2Completed: boolean;
          phase1Evaluation: unknown;
          phase2Evaluation: {
            id: string;
            verdict: string;
            verdictReasoning?: string;
            observations?: string;
            keyDifferences?: string[];
            evaluatorConfidence?: number;
            consistencyMetrics?: Phase2Metrics;
          } | null;
        };
      }>(`/api/validation/test-runs/${id}/stability-evaluation`);
      return response;
    },
    enabled: !!id,
  });

  // Pre-populate form with existing evaluation data
  useEffect(() => {
    const existing = existingEvalData?.currentUser?.phase2Evaluation;
    if (existing) {
      setVerdict(existing.verdict);
      setVerdictReasoning(existing.verdictReasoning || '');
      setObservations(existing.observations || '');
      setKeyDifferences(existing.keyDifferences || []);
      setConfidence(existing.evaluatorConfidence || 3);
    }
  }, [existingEvalData]);

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: async (data: {
      verdict: string;
      verdictReasoning?: string;
      observations?: string;
      keyDifferences: string[];
      evaluatorConfidence: number;
      consistencyMetrics: Phase2Metrics;
    }) => {
      const response = await api.post(
        `/api/validation/test-runs/${id}/stability-evaluation/phase2`,
        data
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', id] });
      queryClient.invalidateQueries({ queryKey: ['validation', 'search-phase', id] });
      queryClient.invalidateQueries({ queryKey: ['validation', 'stability-evaluation', id] });
      navigate(`/validation/test-runs/${id}`);
    },
  });

  const isLoading = resultsLoading || testRunLoading;
  const testRun = testRunData?.data;
  const searchExecutions = ((resultsData?.data?.searchExecutions || []) as SearchExecution[]).filter(
    (e) => e.status === 'COMPLETED'
  );

  // Calculate consistency metrics
  const metrics = useMemo(() => {
    if (searchExecutions.length === 0) return null;
    return calculatePhase2Metrics(searchExecutions);
  }, [searchExecutions]);

  // Add key difference
  const addDifference = () => {
    if (newDifference.trim()) {
      setKeyDifferences((prev) => [...prev, newDifference.trim()]);
      setNewDifference('');
    }
  };

  const removeDifference = (index: number) => {
    setKeyDifferences((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle submit
  const handleSubmit = () => {
    if (!verdict || !metrics) return;

    submitMutation.mutate({
      verdict,
      verdictReasoning: verdictReasoning.trim() || undefined,
      observations: observations.trim() || undefined,
      keyDifferences,
      evaluatorConfidence: confidence,
      consistencyMetrics: metrics,
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!testRun || searchExecutions.length === 0) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
        <AlertCircle className="mx-auto h-10 w-10 text-amber-500" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">No Matching Results</h2>
        <p className="mt-1 text-sm text-gray-500">
          Complete Phase 4: Care Provider Matching before starting stability evaluation.
        </p>
        <Link
          to={`/validation/test-runs/${id}`}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Test Run
        </Link>
      </div>
    );
  }

  // Check if we're in read-only mode (revealed but has evaluation to view)
  const hasExistingEvaluation = !!existingEvalData?.currentUser?.phase2Evaluation;
  const isReadOnly = testRun.isRevealed;

  // If revealed but no evaluation exists, show message
  if (testRun.isRevealed && !hasExistingEvaluation) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
        <Eye className="mx-auto h-10 w-10 text-amber-500" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">Results Already Revealed</h2>
        <p className="mt-1 text-sm text-gray-500">
          This test run has been revealed. No evaluation was submitted for Phase 2.
        </p>
        <Link
          to={`/validation/test-runs/${id}/stability-results`}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
        >
          <Activity className="h-4 w-4" />
          View Results
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate(`/validation/test-runs/${id}`)}
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Matching Stability Evaluation</h1>
            <p className="text-sm text-gray-500">
              {testRun.name} - Evaluate provider consistency across {searchExecutions.length}{' '}
              iterations
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isReadOnly ? (
            <>
              <Eye className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-600">Revealed (Read Only)</span>
            </>
          ) : (
            <>
              <EyeOff className="h-5 w-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-600">Blind Mode</span>
            </>
          )}
        </div>
      </div>

      {/* Test Case Context */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Test Case Context</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <span className="text-xs text-gray-500">Test Case</span>
            <p className="font-medium text-gray-900">{testRun.testCase.name}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Condition</span>
            <p className="text-sm text-gray-900">{testRun.testCase.condition}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Patient</span>
            <p className="text-sm text-gray-900">
              {testRun.testCase.patientInfo.age} y/o {testRun.testCase.patientInfo.gender}
              {testRun.testCase.patientInfo.canton && ` - ${testRun.testCase.patientInfo.canton}`}
            </p>
          </div>
        </div>
      </div>

      {/* Consistency Metrics Summary */}
      {metrics && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <Phase2ConsistencyMetrics metrics={metrics} />
        </div>
      )}

      {/* Provider Overlap Matrix */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Search className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Provider Overlap Matrix</h2>
        </div>
        <ProviderOverlapMatrix executions={searchExecutions} />
      </div>

      {/* Stability Verdict Selection */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Star className="h-5 w-5 text-amber-600" />
          <h2 className="text-lg font-semibold text-gray-900">Matching Stability Verdict</h2>
        </div>

        <div className="grid gap-3 sm:grid-cols-5">
          {STABILITY_VERDICTS.map((v) => {
            const Icon = v.icon;
            return (
              <button
                key={v.value}
                onClick={() => !isReadOnly && setVerdict(v.value)}
                disabled={isReadOnly}
                className={clsx(
                  'flex flex-col items-center gap-2 rounded-lg border-2 p-4 text-center transition-all',
                  verdict === v.value
                    ? v.color + ' ring-2 ring-offset-2'
                    : 'border-gray-200 hover:border-gray-300',
                  isReadOnly && 'cursor-not-allowed opacity-75'
                )}
              >
                <Icon className={clsx('h-6 w-6', verdict === v.value ? '' : 'text-gray-400')} />
                <span className="text-sm font-medium">{v.label}</span>
                <span className="text-xs opacity-75">{v.description}</span>
              </button>
            );
          })}
        </div>

        {/* Verdict Reasoning */}
        <div className="mt-4">
          <label className="text-sm font-medium text-gray-700">Reasoning (optional)</label>
          <textarea
            value={verdictReasoning}
            onChange={(e) => setVerdictReasoning(e.target.value)}
            rows={2}
            placeholder="Explain your matching stability assessment..."
            disabled={isReadOnly}
            className={clsx(
              'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500',
              isReadOnly && 'bg-gray-50 cursor-not-allowed'
            )}
          />
        </div>
      </div>

      {/* Key Differences */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <h2 className="text-lg font-semibold text-gray-900">Key Differences Observed</h2>
          <span className="text-xs text-gray-500">(optional)</span>
        </div>

        <div className="space-y-2">
          {keyDifferences.map((diff, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800"
            >
              <span className="flex-1">{diff}</span>
              {!isReadOnly && (
                <button
                  onClick={() => removeDifference(idx)}
                  className="text-amber-600 hover:text-amber-800"
                >
                  <XCircle className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {!isReadOnly && (
            <div className="flex gap-2">
              <input
                type="text"
                value={newDifference}
                onChange={(e) => setNewDifference(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addDifference()}
                placeholder="Describe a key difference in provider recommendations..."
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
              />
              <button
                onClick={addDifference}
                className="rounded-lg bg-amber-100 px-4 py-2 text-amber-700 hover:bg-amber-200"
              >
                Add
              </button>
            </div>
          )}
          {isReadOnly && keyDifferences.length === 0 && (
            <p className="text-sm text-gray-500 italic">No key differences were recorded</p>
          )}
        </div>
      </div>

      {/* Additional Observations & Confidence */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-4">
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <MessageSquare className="h-4 w-4" />
            Additional Observations (optional)
          </label>
          <textarea
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            rows={3}
            placeholder="Any other observations about matching consistency..."
            disabled={isReadOnly}
            className={clsx(
              'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500',
              isReadOnly && 'bg-gray-50 cursor-not-allowed'
            )}
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Star className="h-4 w-4" />
            Confidence in Assessment: {confidence}/5
          </label>
          <input
            type="range"
            min="1"
            max="5"
            value={confidence}
            onChange={(e) => setConfidence(Number(e.target.value))}
            disabled={isReadOnly}
            className={clsx(
              'mt-2 w-full accent-amber-600',
              isReadOnly && 'cursor-not-allowed opacity-75'
            )}
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>Low confidence</span>
            <span>High confidence</span>
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-6">
        <div className="text-sm text-gray-600">
          {verdict ? (
            <span className="flex items-center gap-2 text-green-600">
              <Check className="h-4 w-4" />
              Verdict {isReadOnly ? 'recorded' : 'selected'}: {STABILITY_VERDICTS.find((v) => v.value === verdict)?.label}
            </span>
          ) : (
            <span className="text-amber-600">
              {isReadOnly ? 'No verdict was recorded' : 'Please select a matching stability verdict'}
            </span>
          )}
        </div>
        {!isReadOnly && (
          <button
            onClick={handleSubmit}
            disabled={!verdict || submitMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            Submit Matching Stability Evaluation
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
        {isReadOnly && (
          <Link
            to={`/validation/test-runs/${id}/stability-results`}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            <Activity className="h-4 w-4" />
            View Results
          </Link>
        )}
      </div>

      {/* Error Display */}
      {submitMutation.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Error</span>
          </div>
          <p className="mt-1 text-sm text-red-600">
            {submitMutation.error instanceof Error
              ? submitMutation.error.message
              : 'An unexpected error occurred'}
          </p>
        </div>
      )}
    </div>
  );
}
